import { VersionGroupModule } from '@/modules/versiongroup';
import { ServerExceptionCode, NotificationType } from './../commonui/utils/enum';
import { sortBy, orderBy } from "lodash";
import { store } from "@/commonui/store/index";
import { VuexModule, Module, Mutation, Action, getModule } from "vuex-module-decorators";
import { TeachingActivityModel, TeachingActivityService, TransitionModel } from "@/services/teaching-activity";
import { VersionGroupService, VersionGroupModel } from "@/services/version-group";
import { TransitionService } from "@/services/transition";
import { MessageHelper, fmtMsg } from "commonui";
import { CommonEntitiesLocale } from "@/locales/localeid";

function showErrorMessage(error_code, isTeachingActivity) {
	if (error_code == ServerExceptionCode.TargetHasExistedException) {
		isTeachingActivity
			? MessageHelper.Message(
					NotificationType.Warning,
					fmtMsg(CommonEntitiesLocale.CommonTeachingActivityTextHasExisted)
			  )
			: MessageHelper.Message(
					NotificationType.Warning,
					fmtMsg(CommonEntitiesLocale.CommonTransitionTextHasExisted)
			  );
	}
}

function clearEntities(callback) {
	if (VersionGroupModule.selectedGsVersion == "-1" && VersionGroupModule.selectedLsVersion == "-1") {
		callback && callback();
		return true;
	}
	return false;
}

@Module({ namespaced: true, name: "CommonEntitiesState", store: store, dynamic: true })
class CommonEntitiesState extends VuexModule {
	transitionService = TransitionService();
	teachingActivityService = TeachingActivityService();
	versionService = VersionGroupService();
	loading = false;
	transitions: TransitionModel[] = [];
	teachingActivities: TeachingActivityModel[] = [];
	public isVisible = false;
	public isEditEntity = false;
	public selectedEntity: any = {};
	public versionGroups = [];
	public isUsageModalVisible = false;
	public selectedVersionGroupId = "";

	@Mutation
	async setTeachingActivites(value) {
		this.teachingActivities = value;
	}

	@Mutation
	resetTeachingActivites(value) {
		this.teachingActivities.push(value);
	}

	@Mutation
	updateTeachingActivites(value) {
		this.teachingActivities.forEach((a) => {
			if (a.id == value.id) {
				Object.assign(a, value);
			}
		});
	}

	@Mutation
	removeTeachingActivity(id) {
		const index = this.teachingActivities.findIndex((a) => a.id == id);
		this.teachingActivities.splice(index, 1);
	}

	@Mutation
	async setTransitions(value) {
		this.transitions = value;
	}

	@Mutation
	resetTransitions(value) {
		this.transitions.push(value);
	}

	@Mutation
	setLoading(value) {
		this.loading = value;
	}

	@Mutation
	updateTransitions(value) {
		this.transitions.forEach((a) => {
			if (a.id == value.id) {
				Object.assign(a, value);
			}
		});
	}

	@Mutation
	removeTransition(id) {
		const index = this.transitions.findIndex((a) => a.id == id);
		this.transitions.splice(index, 1);
	}

	@Mutation
	async setRelatedContents(value) {}

	@Mutation
	setVisibleModal(isVisible) {
		this.isVisible = isVisible;
	}

	@Mutation
	setUsageModalVisible(isVisible) {
		this.isUsageModalVisible = isVisible;
	}

	@Mutation
	setIsEdit(isEdit) {
		this.isEditEntity = isEdit;
	}

	@Mutation
	setSelectedEntity(entity) {
		this.selectedEntity = entity;
	}

	@Mutation
	setVersionGroups(versionGroups) {
		this.versionGroups = versionGroups;
	}
	@Mutation
	setVersionGroupId(versionGroupId) {
		this.selectedVersionGroupId = versionGroupId;
	}

	@Action({ rawError: true })
	async getTeachingActivitiesStatistic() {
		if (
			clearEntities(() => {
				this.setTeachingActivites([]);
			})
		) {
			return;
		}
		const params = {
			isCommon: true,
			versionGroupIds: [VersionGroupModule.selectedGsVersion, VersionGroupModule.selectedLsVersion]
		};
		this.setLoading(true);
		const activities = await this.teachingActivityService.getTeachingActivitiesStatistic(params);
		this.setTeachingActivites(sortBy(activities, "text"));
		this.setLoading(false);
	}

	@Action
	async addTeachingActivity(item) {
		const params = {
			...item,
			isCommon: true,
			text: item.text.trim(),
			versionGroupId: this.selectedVersionGroupId
		};
		this.setLoading(true);
		this.teachingActivityService.createTeachingActivity(params).then((data) => {
			const activity = { ...data, ...params, contentExposures: [] };
			this.resetTeachingActivites(activity);
			this.setLoading(false);
		}).catch(({ error_code }) => {
			showErrorMessage(error_code, true);
		});
	}

	@Action
	async updateTeachingActivity(item) {
		const params = {
			...item,
			isCommon: true,
			text: item.text.trim(),
			versionGroupId: this.selectedVersionGroupId
		};
		this.setLoading(true);
		this.teachingActivityService.updateTeachingActivity(params).then((data) => {
			this.updateTeachingActivites(params);
			this.setLoading(false);
		}).catch(({ error_code }) => {
			showErrorMessage(error_code, true);
		});
	}

	@Action
	async deleteTeachingActivity(item) {
		const params = {
			teachingActivityId: item
		};
		this.setLoading(true);
		this.teachingActivityService.deleteTeachingActivity(params).then((data) => {
			this.removeTeachingActivity(item);
			this.setLoading(false);
		});
	}

	@Action
	async getVersionGroups() {
		const versionGroups: VersionGroupModel[] = [];
		const gsVersion = VersionGroupModule.versionGroups.find((v) => v.id == VersionGroupModule.selectedGsVersion);
		const lsVersion = VersionGroupModule.versionGroups.find((v) => v.id == VersionGroupModule.selectedLsVersion);
		gsVersion && versionGroups.push(gsVersion);
		lsVersion && versionGroups.push(lsVersion);
		const sortedVersionGroups = orderBy(versionGroups, ["curriculumType", "name"], ["asc", "asc"]);
		this.setVersionGroups(sortedVersionGroups);
		this.setVersionGroupId(sortedVersionGroups.length && sortedVersionGroups[0].id);
	}

	@Action({ rawError: true })
	async getTransitionsStatistic() {
		if (
			clearEntities(() => {
				this.setTransitions([]);
			})
		) {
			return;
		}
		const params = {
			isCommon: true,
			versionGroupIds: [VersionGroupModule.selectedGsVersion, VersionGroupModule.selectedLsVersion]
		};
		this.setLoading(true);
		const transitions = await this.transitionService.getTransitionsStatistic(params);
		this.setTransitions(sortBy(transitions, "text"));
		this.setLoading(false);
	}

	@Action
	async addTransition(item) {
		const params = {
			...item,
			isCommon: true,
			text: item.text.trim(),
			versionGroupId: this.selectedVersionGroupId
		};
		this.setLoading(true);
		this.transitionService.createTransition(params).then((data) => {
			const transition = { ...data, ...params, totalCount: 0 };
			this.resetTransitions(transition);
			this.setLoading(false);
		}).catch(({ error_code }) => {
			showErrorMessage(error_code, false);
		});
	}

	@Action
	async updateTransition(item) {
		const params = {
			...item,
			isCommon: true,
			text: item.text.trim(),
			versionGroupId: this.selectedVersionGroupId
		};
		this.setLoading(true);
		this.transitionService.updateTransition(params).then((data) => {
			this.updateTransitions(params);
			this.setLoading(false);
		}).catch(({ error_code }) => {
			showErrorMessage(error_code, false);
		});
	}

	@Action
	async deleteTransition(item) {
		const params = {
			transitionId: item
		};
		this.setLoading(true);
		this.transitionService.deleteTransition(params).then((data) => {
			this.removeTransition(item);
			this.setLoading(false);
		});
	}
}

export const CommonEntitiesModule = getModule(CommonEntitiesState);
